import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { Link } from 'wouter';
import { LandingLayout } from '../shared/LandingLayout';
import { SEOHead } from '../shared/SEOHead';
import { LandingCTA } from '../shared/LandingCTA';
import { TrustSection } from '../shared/TrustSection';
import {
  generateCanonicalUrl,
  combineSchemas,
  generateOrganizationSchema,
  generateWebPageSchema,
} from '@/utils/seo';

export interface ComparisonFeature {
  feature: string;
  sloty: boolean | string;
  competitor: boolean | string;
}

interface ComparisonPageProps {
  competitorKey: string;
  competitorName: string;
}

function CellValue({ value }: { value: boolean | string }) {
  if (typeof value === 'string') {
    return <span className="text-sm text-slate-700">{value}</span>;
  }
  return value ? (
    <Check className="w-5 h-5 text-green-500 mx-auto" />
  ) : (
    <X className="w-5 h-5 text-slate-300 mx-auto" />
  );
}

export default function ComparisonPage({ competitorKey, competitorName }: ComparisonPageProps) {
  const { t } = useTranslation(['compare', 'common']);

  const structuredData = combineSchemas(
    generateOrganizationSchema(),
    generateWebPageSchema({
      name: `Sloty vs ${competitorName} - Booking Software Comparison`,
      description: t(`compare:${competitorKey}.description`),
      url: generateCanonicalUrl(`/compare/${competitorKey}`),
    })
  );

  const features = t(`compare:${competitorKey}.features`, { returnObjects: true }) as ComparisonFeature[];

  const reasons = t(`compare:${competitorKey}.reasons`, { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;

  return (
    <LandingLayout transparentHeader>
      <SEOHead
        title={t(`common:seo.compare.${competitorKey}.title`)}
        description={t(`common:seo.compare.${competitorKey}.description`)}
        keywords={`sloty vs ${competitorName.toLowerCase()}, ${competitorName.toLowerCase()} alternative, booking software comparison, appointment scheduling comparison`}
        canonical={generateCanonicalUrl(`/compare/${competitorKey}`)}
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className="py-20 sm:py-28 bg-gradient-to-b from-brand-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
            {t(`compare:${competitorKey}.title`)}
          </h1>
          <p className="mt-6 text-xl text-slate-600 max-w-2xl mx-auto">
            {t(`compare:${competitorKey}.subtitle`)}
          </p>
          <div className="mt-10">
            <Link
              href="/register"
              className="inline-flex items-center justify-center rounded-full py-3 px-8 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-500"
            >
              {t('compare:common.tryCta')}
            </Link>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            {t('compare:common.tableTitle')}
          </h2>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border">
            <table className="w-full">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    {t('compare:common.feature')}
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-brand-600 bg-brand-50">
                    Sloty
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    {competitorName}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {features.map((item, index) => (
                  <tr key={index} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-900">{item.feature}</td>
                    <td className="px-6 py-4 text-center bg-brand-50/50">
                      <CellValue value={item.sloty} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <CellValue value={item.competitor} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <LandingCTA
        title={t('compare:common.midPageCta.title')}
        subtitle={t('compare:common.midPageCta.subtitle')}
        ctaPrimary={t('compare:common.midPageCta.button')}
        ctaPrimaryHref="/register"
      />

      {/* Why Switch */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            {t(`compare:${competitorKey}.whySwitch`)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reasons.map((reason, index) => (
              <div key={index} className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{reason.title}</h3>
                <p className="text-slate-600">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrustSection />

      <LandingCTA
        title={t('compare:common.ctaTitle')}
        subtitle={t('compare:common.ctaSubtitle')}
        ctaPrimary={t('compare:common.ctaButton')}
        ctaPrimaryHref="/register"
        ctaSecondary={t('compare:common.ctaSecondary')}
        ctaSecondaryHref="/features"
        variant="gradient"
      />
    </LandingLayout>
  );
}
